import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal, untracked } from '@angular/core';

import { WorkflowConsent, WorkflowConsentContent } from '@shared/models';

/**
 * The consents a customer owes on a screen, as Document Acceptance returns them (KOR-15237): one checkbox per
 * consent, its notice above it and every document it covers linked under it. All text is shown exactly as sent,
 * as plain text. Reports which consents are ticked; deciding what that allows is the screen's job.
 *
 * A consent with `requiresDocumentOpen` can't be ticked until each linked document was opened through its link.
 * Opening the link is what Document Acceptance records, and it refuses the consent without it; this only keeps the
 * checkbox from promising otherwise.
 */
@Component({
  selector: 'app-consent-list',
  templateUrl: './consent-list.component.html',
  styleUrls: ['./consent-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ConsentListComponent {
  consents = input.required<WorkflowConsent[]>();

  tickedChange = output<WorkflowConsent[]>();

  ordered = computed(() => [...this.consents()].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)));

  /** Keyed by presentation token: a fresh set from DAD carries new tokens, so nothing ticked before carries over. */
  private tickedTokens = signal<ReadonlySet<string>>(new Set());

  /** `<presentation token>|<document id>`: the links belong to one presentation, so a fresh set needs them opened again. */
  private openedDocuments = signal<ReadonlySet<string>>(new Set());

  constructor() {
    // A fresh set (e.g. after an expired token): tell the screen nothing is ticked any more.
    effect(
      () => {
        this.consents();
        untracked(() => {
          this.tickedTokens.set(new Set());
          this.openedDocuments.set(new Set());
          this.tickedChange.emit([]);
        });
      },
      { allowSignalWrites: true },
    );
  }

  isTicked = (consent: WorkflowConsent): boolean => this.tickedTokens().has(consent.presentationToken);

  isRequired = (consent: WorkflowConsent): boolean => consent.required !== false;

  /** Still waiting for a linked document to be opened. */
  isLocked = (consent: WorkflowConsent): boolean =>
    this.documentsToOpen(consent).some(({ documentId }) => !this.openedDocuments().has(this.openKey(consent, documentId)));

  markOpened = (consent: WorkflowConsent, document: WorkflowConsentContent): void => {
    this.openedDocuments.set(new Set(this.openedDocuments()).add(this.openKey(consent, document.documentId)));
  };

  /** A middle-click opens the link in a new tab; a right-click only opens the menu. */
  markOpenedByMiddleClick = (event: MouseEvent, consent: WorkflowConsent, document: WorkflowConsentContent): void => {
    if (event.button === 1) {
      this.markOpened(consent, document);
    }
  };

  /** A notice shown as text needs no link, unless it's the document the consent requires opened. */
  noticeLink = (consent: WorkflowConsent): WorkflowConsentContent | undefined =>
    consent.notice?.url && (!consent.notice.text || this.noticeMustBeOpened(consent)) ? consent.notice : undefined;

  toggle = (consent: WorkflowConsent, checked: boolean): void => {
    if (checked && this.isLocked(consent)) {
      return;
    }
    const tokens = new Set(this.tickedTokens());
    if (checked) {
      tokens.add(consent.presentationToken);
    } else {
      tokens.delete(consent.presentationToken);
    }
    this.tickedTokens.set(tokens);
    this.tickedChange.emit(this.ordered().filter(({ presentationToken }) => tokens.has(presentationToken)));
  };

  describedBy = (consent: WorkflowConsent, index: number): string | null => {
    const ids = [consent.notice?.text ? this.noticeId(index) : null, this.isLocked(consent) ? this.openHintId(index) : null];
    return ids.filter((id) => id !== null).join(' ') || null;
  };

  inputId = (index: number): string => `consent-${index}`;

  noticeId = (index: number): string => `consent-${index}-notice`;

  openHintId = (index: number): string => `consent-${index}-open-hint`;

  // The same documents Document Acceptance requires opened: the consent's own, or its linked notice when it has none.
  private documentsToOpen = (consent: WorkflowConsent): WorkflowConsentContent[] => {
    if (!consent.requiresDocumentOpen) {
      return [];
    }
    if (consent.documents?.length) {
      return consent.documents;
    }
    return consent.notice ? [consent.notice] : [];
  };

  private noticeMustBeOpened = (consent: WorkflowConsent): boolean => !!consent.requiresDocumentOpen && !consent.documents?.length;

  private openKey = (consent: WorkflowConsent, documentId: string): string => `${consent.presentationToken}|${documentId}`;
}
